import { inject, Injectable } from '@angular/core';

import { PatientVisitDataSteam } from '@shared/services/datasource.service';
import { GqlService, PatientProtocolType } from '@shared/services/gql.service';
import { OverlayService } from '@shared/services/overlay.service';

import { PatientTrackerRow, VisitSchedule } from '../types';
import {
  isVisitMonthAfterCurrentOpenMonth,
  isVisitMonthBeforeCurrentOpenMonth,
  isVisitMonthEqualWithCurrentOpenMonth,
} from '../utils';

@Injectable({ providedIn: 'root' })
export class PatientTrackerScheduleService {
  private gqlService = inject(GqlService);
  private overlayService = inject(OverlayService);

  private getPatientCosts(
    visits: VisitSchedule[],
    patientProtocolVersion: string
  ): Omit<
    PatientTrackerRow,
    'contractCurrency' | 'currency' | 'external_patient_id' | 'site_id' | 'site_no'
  > {
    return visits.reduce(
      // eslint-disable-next-line complexity
      (accum, { completed, cost, patient_protocol_type, patient_protocol_version_id }) => {
        const isPatientVisitType =
          patient_protocol_type === PatientProtocolType.PATIENT_PROTOCOL_PATIENT_VISIT;

        if (isPatientVisitType && completed) {
          accum.totalVisitCostsToDate += cost.amount || 0;
          accum.totalVisitCostsToDateContracted += cost.contract_amount || 0;
        }

        if (completed && patient_protocol_version_id !== patientProtocolVersion) {
          accum.totalCostsFromOtherVersions += cost.amount || 0;
          accum.totalCostsFromOtherVersionsContracted += cost.contract_amount || 0;
        }

        if (!isPatientVisitType && completed) {
          accum.totalInvoiceablesToDate += cost.amount || 0;
          accum.totalInvoiceablesToDateContracted += cost.contract_amount || 0;
        }

        if (completed) {
          accum.totalLTDCosts += cost.amount || 0;
          accum.totalLTDCostsContracted += cost.contract_amount || 0;
        }

        accum.totalForecastCostThroughEot += cost.amount || 0;
        accum.totalForecastCostThroughEotContracted += cost.contract_amount || 0;

        if (!completed) {
          accum.forecastRemaining += cost.amount || 0;
          accum.forecastRemainingContracted += cost.contract_amount || 0;
        }

        return accum;
      },
      {
        forecastRemaining: 0,
        forecastRemainingContracted: 0,
        totalCostsFromOtherVersions: 0,
        totalCostsFromOtherVersionsContracted: 0,
        totalForecastCostThroughEot: 0,
        totalForecastCostThroughEotContracted: 0,
        totalInvoiceablesToDate: 0,
        totalInvoiceablesToDateContracted: 0,
        totalLTDCosts: 0,
        totalLTDCostsContracted: 0,
        totalVisitCostsToDate: 0,
        totalVisitCostsToDateContracted: 0,
      }
    );
  }

  private getVisits(visits: VisitSchedule[], currentOpenMonth: string) {
    return visits.reduce((accum, visit) => {
      const isPlannedVisitThroughOpenMonth = this.isPlannedVisitThroughOpenMonth(
        visit,
        currentOpenMonth
      );

      const isPlanned =
        (visit.scheduled_date
          ? isVisitMonthAfterCurrentOpenMonth(visit.scheduled_date, currentOpenMonth)
          : !!visit.cost) &&
        !visit.completed &&
        !isPlannedVisitThroughOpenMonth &&
        visit.patient_protocol_type === PatientProtocolType.PATIENT_PROTOCOL_PATIENT_VISIT;

      return {
        ...accum,
        [`${visit.patient_protocol_id}::completed`]: visit.completed,
        [`${visit.patient_protocol_id}::costs::contracted`]: visit.cost.contract_amount,
        [`${visit.patient_protocol_id}::costs`]: visit.cost.amount,
        [`${visit.patient_protocol_id}::dates`]: visit.scheduled_date,
        [`${visit.patient_protocol_id}::isPlannedThroughEOT`]:
          isPlanned && !visit.completed && !isPlannedVisitThroughOpenMonth,
        [`${visit.patient_protocol_id}::shouldHighlight`]: isPlannedVisitThroughOpenMonth,
      };
    }, {});
  }

  isPlannedVisitThroughOpenMonth(visit: VisitSchedule, currentOpenMonth: string) {
    const isCurrentMonth = visit.scheduled_date
      ? isVisitMonthEqualWithCurrentOpenMonth(visit.scheduled_date, currentOpenMonth) ||
        isVisitMonthBeforeCurrentOpenMonth(visit.scheduled_date, currentOpenMonth)
      : false;

    return isCurrentMonth && !visit.completed;
  }

  parsePatientVisitSchedules(
    patientProtocolVersion: string,
    currentOpenMonth: string,
    listSchedules: PatientVisitDataSteam[]
  ): PatientTrackerRow[] {
    return listSchedules.map((patientSchedule) => {
      return {
        // Patient Costs
        ...this.getVisits(
          patientSchedule.visit_schedule.filter(
            ({ patient_protocol_version_id }) =>
              patient_protocol_version_id === patientProtocolVersion
          ),
          currentOpenMonth
        ),
        external_patient_id: patientSchedule.external_patient_id,
        site_id: patientSchedule.site_id,
        site_no: patientSchedule.site_no,
        // Patient Costs column
        ...this.getPatientCosts(patientSchedule.visit_schedule, patientProtocolVersion),
        contractCurrency: patientSchedule.visit_schedule[0].cost.contract_currency || '',
        currency: patientSchedule.visit_schedule[0].cost.amount_currency || '',
      };
    });
  }
}
